import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { firstValueFrom, from, of } from 'rxjs';
import { catchError, filter, switchMap, take, timeout } from 'rxjs/operators';
import { ADPResourceService, ResourcesLoaderService } from 'digital-core';
import { dashboardRouteWithSlash, PGHAppLoadService } from 'src/app/core/core';
import { LoggedInUserSettings } from 'src/app/core/services/settings/logged-in-user-settings';
import { PlanRenewalService } from 'src/app/shared/components/plan-renewal-notice';
import { PlanDetail } from 'src/app/model/plan/plan-detail';

/** Max time to wait for the selected plan detail to load before failing open. */
const PLAN_DETAIL_TIMEOUT_MS = 10000;

/**
 * Blocks the service-request route once a renewable plan has fully lapsed.
 *
 * Functional guard (not root-provided) so it runs in the route's injection context and
 * can resolve the route-scoped `PlanRenewalService` (listed in the route's `providers`),
 * which owns all the renewal computation. The guard itself only handles the cold-load
 * bootstrap the service can't do, then delegates the decision:
 *   1. Renewal enabled? (`isRenewPlanEnabled`) — else allow.
 *   2. Core certificate present? — else allow.
 *   3. Status in the blocked list? (`!isValidPlanDetail`) — block immediately.
 *   4. Otherwise grace check: block once coverage is past the grace window.
 */
export const ServiceRequestActivateGuard: CanActivateFn = async (): Promise<boolean | UrlTree> => {
  const renewal = inject(PlanRenewalService);
  const appLoadService = inject(PGHAppLoadService);
  const resourcesLoader = inject(ResourcesLoaderService);
  const resourceService = inject(ADPResourceService);
  const loggedInUserSettings = inject(LoggedInUserSettings);
  const router = inject(Router);

  await ensureSiteResourcesLoaded(appLoadService, resourcesLoader, resourceService);

  // Ensure the plan detail has loaded so the renewal signals below are populated.
  await waitForPlanDetail(loggedInUserSettings);

  if (!renewal.planCoreCertificate()) {
    return true;
  }

  if (!renewal.isRenewPlanEnabled()) {
    return true;
  }

  const dashboard = router.createUrlTree([dashboardRouteWithSlash]);

  // isValidPlanDetail() is false when the status is in Global_Renewal_Blocked_Plan_Statuses
  // (e.g. Cancelled) — those are denied immediately, with no grace period.
  if (!renewal.isValidPlanDetail()) {
    return dashboard;
  }

  // Not blocked: allow while still inside the grace window, redirect to the dashboard once
  // it has ended.
  const isGracePeriodEnded = renewal.isGracePeriodEnded();
  return isGracePeriodEnded ? dashboard : true;
};

/**
 * canActivate runs BEFORE the route resolvers (SiteConfigResolver / PageResourcesResolver),
 * so on a cold load / refresh the global settings + labels aren't populated yet — the
 * renewal signals would read null. We can't wait on `isSiteResourcesLoaded` (it only flips
 * true inside PageResourcesResolver, which runs AFTER this guard — that would deadlock the
 * navigation / blank the page). Instead we drive the load ourselves, mirroring the resolvers:
 * loadSiteResources for portal settings, loadAllPageResources('Global') for the Global_* page
 * labels (which live in homepageResources.portalLabels, not in loadSiteResources' output).
 * Warm navigations short-circuit. Failures fail-open so a load error never blocks navigation.
 */
async function ensureSiteResourcesLoaded(appLoadService: PGHAppLoadService, resourcesLoader: ResourcesLoaderService, resourceService: ADPResourceService): Promise<void> {
  if (appLoadService.isSiteResourcesLoaded.getValue()) {
    return;
  }
  try {
    await firstValueFrom(from(window['__bootstrapConfigPromise'] ?? Promise.resolve()).pipe(switchMap(() => appLoadService.loadSiteResources())));
    const pageResources = await firstValueFrom(resourcesLoader.loadAllPageResources('Global', false, true));
    resourceService.setGlobalResources(pageResources.resources);
    resourceService.setGlobalLabels(pageResources.labels);
    resourceService.setPortalPageList(pageResources.lists);
    // Flag the load complete so consumers gated on isSiteResourcesLoaded kick off — most
    // importantly LoggedInUserSettings' plan-detail pipeline, which feeds waitForPlanDetail.
    appLoadService.isSiteResourcesLoaded.next(true);
  } catch {
    // Fail-open: if resources can't be loaded, let the user through rather than blank the page.
  }
}

/**
 * The selected plan detail loads asynchronously (LoggedInUserSettings resolves it once site
 * resources + auth are ready), so it is typically still null at canActivate time. Wait for the
 * first non-null emission, bounded by a timeout. Fail-open (null) on timeout or error so a slow
 * or failed plan fetch never blocks navigation.
 */
async function waitForPlanDetail(loggedInUserSettings: LoggedInUserSettings): Promise<PlanDetail | null> {
  const current = loggedInUserSettings.$selectedPlanDetail.getValue();
  if (current) {
    return current;
  }
  return firstValueFrom(
    loggedInUserSettings.$selectedPlanDetail.pipe(
      filter((detail): detail is PlanDetail => detail !== null),
      take(1),
      timeout(PLAN_DETAIL_TIMEOUT_MS),
      catchError(() => of(null))
    )
  );
}
